import clsx from 'clsx';

import Image from 'next/image';
import Link from '@/components/Link';
import Rating from '@base/components/src/Rating';
import { calcAGDrating } from '@/utils/rating';
import { TopSellingCardProps } from './types';
import SearchResultsPrice from './components/SearchResultsPrice';
import AddToCartButton from '@/components/Button/AddToCartButton';
import RadioButton from '@/components/RadioButton';
import BaseButton from '@/components/Button/BaseButton';
import styles from './TopSellingCard.module.css';

const TopSellingCard = ({
  searchItem,
  className,
  idx,
  priorityIndex = 2,
  addToCartButton,
  isRadioSelect,
  isSelected,
  isRemoveable,
  onClick,
  onContextMenu,
  handleAddToCart,
  onViewAdapter,
  onDeselect,
}: TopSellingCardProps) => {
  const {
    imageId,
    linkId,
    name,
    newPriceMin,
    newPriceMax,
    newSalePriceMin,
    newSalePriceMax,
    numReviews,
    priceCents,
    pricePerUnit,
    pricePerUnitLabel,
    productConditionMax,
    productCondition,
    rating,
    salePriceCents,
    usedPriceCents,
    id,
    relatedProductsImperative,
    productType,
    serviceId,
    serviceType,
    purchasable,
    addToCartButtonText,
    addToCartOptionId,
    tidBit,
  } = searchItem;

  const handleCardClick = () =>
    onClick?.(searchItem?.clickTrackingURL, linkId, relatedProductsImperative, serviceId, serviceType, productType, id);

  return (
    <div className={clsx(styles.searchItemContainer, className)} data-test-type={productType}>
      <Link
        data-testid={`top-selling-cart-link-${idx}`}
        showCursorPointer
        href={linkId}
        classes={clsx(styles.linkCard, { [styles.linkFlex]: serviceType })}
        onClick={(ev: any) => {
          ev.preventDefault();
          if (isSelected && onDeselect && isRemoveable) {
            onDeselect();
          } else {
            handleCardClick();
          }
        }}
        onContextMenu={() => {
          if (onContextMenu) {
            onContextMenu(searchItem?.clickTrackingURL);
          }
        }}
      >
        <div
          className={clsx(styles.searchItemImageContainer, { [styles.radioSelect]: isRadioSelect })}
          data-selected={isSelected}
        >
          {isRadioSelect && (
            <RadioButton
              radioLabelClassName={styles.cardRadioLabel}
              selected={!!isSelected}
              value={id}
              onChange={handleCardClick}
            />
          )}
          <Image
            priority={priorityIndex === -1 ? false : idx <= priorityIndex}
            src={imageId}
            width={400}
            height={300}
            alt={name}
            className={styles.searchItemImage}
          />
          {searchItem?.new && (
            <span className={styles.newImage}>
              <Image src={'/marketplace-static/images/new_icon.png'} fill alt="new" />
            </span>
          )}
          {salePriceCents > 0 && (
            <span className={styles.saleImage}>
              <Image src={'/marketplace-static/images/sale.svg'} fill alt="sale" />
            </span>
          )}
        </div>
        {!serviceType && (
          <Rating
            reviewers={numReviews || 0}
            defaultValue={calcAGDrating(rating) || 0}
            ratingContainerClasses={styles.rating}
            ratingTextClasses={styles.ratingText}
            hideRating={true}
          />
        )}
        <p className={clsx(styles.searchItemName, styles.nameClamp, 'cardName')}>{name}</p>
        <SearchResultsPrice
          pricePerUnit={pricePerUnit}
          priceCents={priceCents}
          salePriceCents={salePriceCents}
          pricePerUnitLabel={pricePerUnitLabel}
          NewPriceMax={newPriceMax}
          NewPriceMin={newPriceMin}
          NewSalePriceMax={newSalePriceMax}
          NewSalePriceMin={newSalePriceMin}
          ProductConditionMax={productConditionMax}
          ProductCondition={productCondition}
          UsedAnyPriceMin={usedPriceCents}
        />
      </Link>
      {tidBit && (
        <p className={styles.tidBit}>
          <span className={styles.tidBitText} title={tidBit}>
            {tidBit}
          </span>
        </p>
      )}
      {isRemoveable ? (
        <BaseButton
          id="radio-select-button"
          label={isSelected ? 'Remove' : 'Select'}
          classes={clsx(isSelected ? 'btnSecondary' : 'btnBase', styles.radioSelectButton, {
            [styles.radioSelectButtonSelected]: isSelected,
          })}
          onClick={(e) => {
            e.preventDefault();
            if (isSelected && onDeselect) {
              onDeselect();
            } else {
              handleCardClick();
            }
          }}
        />
      ) : null}
      {addToCartButton ? (
        <AddToCartButton
          id="top-selling-card-add-to-cart-btn"
          handleAddToCart={async () => {
            if (Number(addToCartOptionId) === 7) {
              onViewAdapter && (await onViewAdapter(id));
              return;
            }
            if (Number(addToCartOptionId) === 6) {
              window.location.href = 'https://www.pyramydair.com/contact-us';
              return;
            }
            if (!purchasable) {
              window.location.href = linkId;
              return;
            }
            if (handleAddToCart) {
              fetch(searchItem.clickTrackingURL || '').catch((error) =>
                console.error(
                  'can not send tracking url data to algonomy from algonomy carousel',
                  error,
                  searchItem.clickTrackingURL,
                ),
              );
              await handleAddToCart(
                id,
                priceCents,
                '1',
                productType,
                serviceId,
                serviceType,
                relatedProductsImperative,
                searchItem?.clickTrackingURL,
              );
            }
          }}
          addToCartOption={{
            id: Number(addToCartOptionId),
            purchasable,
            text: addToCartButtonText,
          }}
        />
      ) : null}
    </div>
  );
};

export default TopSellingCard;
